"use client";

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useStaffTimeTracking } from "@/context/StaffTimeTrackingContext";
import { Play, Square, Loader2 } from "lucide-react";

export function TimeTrackingWidget() {
  const {
    currentSession,
    loading,
    startTracking,
    endTracking,
  } = useStaffTimeTracking();

  const statusBadge = useMemo(() => {
    if (!currentSession) {
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-700">
          No Active Session
        </Badge>
      );
    }

    switch (currentSession.status) {
      case "ACTIVE":
        return (
          <Badge className="bg-green-500 text-white">
            <div className="mr-1 h-2 w-2 rounded-full bg-white animate-pulse" />
            Active
          </Badge>
        );
      case "ENDED":
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700">
            Ended
          </Badge>
        );
      default:
        return null;
    }
  }, [currentSession]);

  const handleStart = async () => {
    await startTracking();
  };

  const handleEnd = async () => {
    if (window.confirm("Are you sure you want to end this session? This action cannot be undone.")) {
      await endTracking();
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Time Tracking</CardTitle>
            <CardDescription>Track your working hours</CardDescription>
          </div>
          {statusBadge}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentSession ? (
          <div className="flex gap-2 justify-center py-4">
            {currentSession.status === "ACTIVE" && (
              <Button
                onClick={handleEnd}
                disabled={loading}
                variant="destructive"
                size="sm"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Square className="mr-2 h-4 w-4" />
                )}
                Work End
              </Button>
            )}

            {currentSession.status === "ENDED" && (
              <div className="text-center w-full">
                <p className="text-sm text-muted-foreground">
                  Session ended
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-4">No active session. Start tracking your time.</p>
            <Button
              onClick={handleStart}
              disabled={loading}
              size="sm"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Work Start
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

